import {useEffect, useState, useRef, useMemo} from "react"
import {Navigate, useLocation, Outlet, matchRoutes, useNavigation, useRouteLoaderData} from "react-router-dom"
import {IdleTimeoutProvider, useIdleTimeoutContext} from "react-idle-timeout"
import {SWRConfig} from "swr"
import {ErrorBoundary} from "@datadog/browser-rum-react"

import ToastMessage from "../utilities/ToastMessage"
import NavBar from "./navigation/NavBar"
import Layout from "./Layout"
import ErrorPage from "./ErrorPage"
import SessionKeepAlive from "./SessionKeepAlive"
import TimeoutWarning from "./TimeoutWarning"
import VersionCheck from "./VersionCheck"
import {getToken, getJwtExpiryTime, removeToken} from "../utilities/useToken"
import {wrappedGet, wrappedPost} from "../utilities/AxiosHelper"
import {routes} from "../routes"
import pkg from "../../package.json"

export const fetchData = async () => {
    const [resp1] = await Promise.all([wrappedGet("/globals")])
    return {
        darkModeFlag: resp1.data.results.dark_mode_flag,
        idleTimeoutInMs: resp1.data.results.idle_timeout_in_ms,
        idleWarningTimeoutInMs: resp1.data.results.idle_warning_timeout_in_ms,
    }
}

function InternalErrorChecker({internalError}) {
    if (internalError) {
        throw new Error("Internal Error")
    }
}

function ignoreStaleResponsesMiddleware(lastNavigationTimeRef) {
    return (useSWRNext) => {
        return (key, fetcher, config) => {
            const requestTime = Date.now()

            const wrappedFetcher = async (...args) => {
                const result = await fetcher(...args)
                if (result) {
                    result.__requestTime = requestTime
                }
                return result
            }

            return useSWRNext(key, wrappedFetcher, {
                ...config,
                onSuccess: (result, key, cfg) => {
                    if (
                        !cfg.skipGlobalOnSuccess &&
                        (!result?.__requestTime || result?.__requestTime >= lastNavigationTimeRef.current)
                    ) {
                        if (result) {
                            config?.onSuccess?.(result.__data, key, cfg)
                        }
                    } else {
                        console.log("Ignored stale SWR response", key)
                    }
                },
            })
        }
    }
}

function AuthenticatedRoot(_props) {
    const {role} = getToken() || {}
    const [permissions, setPermissions] = useState()
    const [error, setError] = useState(false)
    const [internalError, setInternalError] = useState(false)
    const [darkMode, setDarkMode] = useState(null)
    // Use modal data as a global way to display a modal to enable full page faded background
    const [_modalData, setModalData] = useState(false)

    const year = new Date().getFullYear()
    const {darkModeFlag, idleTimeoutInMs, idleWarningTimeoutInMs} = useRouteLoaderData("AuthenticatedRoot")
    // Need to force an update to the swrConfig when location changes
    // to avoid bug where swr does not requery when a user clicks
    // a link that is the same page as what they are on
    const uniqueKey = useMemo(() => crypto.randomUUID(), [])

    useEffect(() => {
        wrappedGet("permissions").then(
            (response) => {
                setPermissions(response.data.results)
            },
            (error) => {
                console.log("Error getting permissions", error)
                setError(true)
            }
        )
    }, [])

    const location = useLocation()

    const handleDarkMode = (darkMode) => {
        if (darkMode) {
            document.documentElement.setAttribute("data-bs-theme", "dark")
        } else {
            document.documentElement.setAttribute("data-bs-theme", "light")
        }
    }

    useEffect(() => {
        if (darkModeFlag === true || darkModeFlag === false) {
            setDarkMode(darkModeFlag)
        }
    }, [darkModeFlag])

    useEffect(() => {
        handleDarkMode(darkMode)
    }, [darkMode])

    useEffect(() => {
        wrappedPost("user-activity", {pathname: location.pathname})
    }, [location, role])

    if (error) {
        throw new Error("External Error")
    }

    const navigation = useNavigation()
    useEffect(() => {
        if (navigation.state === "loading") {
            // Reset loading prior to rendering next page
            setInternalError(false)
            lastNavigationTimeRef.current = Date.now()
        }
    }, [navigation.state])

    const matchedRoutes = matchRoutes(routes, location)
    const currentRoute = matchedRoutes[matchedRoutes.length - 1]
    const permission = currentRoute.route["permission"]
    const hideFooter = currentRoute.route["hideFooter"]
    const hideNavBar = currentRoute.route["hideNavBar"]
    const fullPage = hideNavBar && hideFooter

    const currentPath = navigation.location?.pathname || currentRoute.pathname
    const currentSearch = navigation.location?.search ?? location.search

    const lastNavigationTimeRef = useRef(Date.now())
    const staleMiddleware = ignoreStaleResponsesMiddleware(lastNavigationTimeRef)

    const outletContext = {
        setLoading: false,
        showLoading: false,
        setModalData: setModalData,
        permissions: permissions,
        darkMode: darkMode,
        setDarkMode: setDarkMode,
    }

    if (permissions === null || permissions === undefined) {
        return <></>
    } else if (location.pathname !== "/home" && !!permission && !permissions?.includes(permission)) {
        return <Navigate to="/home" />
    } else {
        /* Uncomment below to see loading state changes */
        /*
        console.log(
            `navigation.state (${navigation.state}) === "loading" || ` +
                `loading (${loading}) || ` +
                `loadingType (${loadingType}) !== "none" && initialLoad (${initialLoad}) || ` +
                `loadingType (${loadingType}) === "all" && requestCount (${requestCount}) > 0 = ` +
                `${showLoading}`
        )
        */

        return (
            <IdleTimeoutProvider
                timeout={idleTimeoutInMs} // idle timeout in milliseconds (15 minutes = 15*60*1000)
                warningDuration={idleWarningTimeoutInMs}
                onIdle={() => {
                    // when the idle period elapses then force a logout.
                    // Hard redirect so no previous user's state stays in memory.
                    wrappedPost("/logout")
                        .catch(() => {})
                        .finally(() => {
                            removeToken()
                            window.location.replace("/login")
                        })
                }}
            >
                <SessionKeepAlive />
                <SWRConfig
                    key={uniqueKey}
                    value={{
                        revalidateOnFocus: false,
                        revalidateOnReconnect: false,
                        shouldRetryOnError: false,
                        refreshWhenHidden: true,
                        onError: (error, key) => {
                            console.error("Error occurred during fetch", error, key)
                            if (!location.pathname.includes("board-view")) {
                                setInternalError(true)
                            }
                        },
                        use: [staleMiddleware],
                    }}
                >
                    <div
                        className={
                            fullPage
                                ? "d-flex flex-column vh-100"
                                : "header-fixed header-tablet-and-mobile-fixed toolbar-enabled toolbar-fixed"
                        }
                    >
                        <div className="d-flex flex-column flex-root">
                            <div className="page d-flex flex-row flex-column-fluid">
                                <ToastMessage />
                                <div className="wrapper d-flex flex-column flex-row-fluid">
                                    {!hideNavBar && (
                                        <NavBar
                                            permissions={permissions}
                                            currentPath={currentPath}
                                            currentSearch={currentSearch}
                                            setModalData={setModalData}
                                            darkMode={darkMode}
                                            setDarkMode={setDarkMode}
                                        />
                                    )}
                                    <ErrorBoundary fallback={() => <ErrorPage />} key={location.pathname}>
                                        <InternalErrorChecker internalError={internalError} />
                                        <VersionCheck />
                                        {fullPage ? (
                                            <Outlet key={location.key} context={outletContext} />
                                        ) : (
                                            <Layout>
                                                <div className="mb-4 d-block">
                                                    <Outlet key={location.key} context={outletContext} />
                                                </div>
                                            </Layout>
                                        )}
                                    </ErrorBoundary>
                                    {!hideFooter && (
                                        <div className="d-flex flex-center py-5">
                                            <div className="text-gray-700 d-flex flex-column flex-stack">
                                                <span className="fw-semibold me-1">
                                                    &copy; {year} All Rights Reserved.
                                                </span>
                                                <span>University of Maryland Medical System.</span>
                                                <span className="fw-semibold me-1">
                                                    OARS Version {pkg.version}
                                                    {role?.toLowerCase() === "enterprise admin" &&
                                                        `:${window.__BUILD_META__?.GIT_HASH}`}{" "}
                                                    | An iHarbor product
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <TimeoutWarning />
                </SWRConfig>
            </IdleTimeoutProvider>
        )
    }
}

export default AuthenticatedRoot
